import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { activityService, materialService, machineryService, toolsService, hrService } from '../services';
import {
  ClipboardList, Package, Wrench, Settings,
  HardHat, Building2, TrendingUp, AlertCircle
} from 'lucide-react';

interface StatCard {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
}

const StatCard = ({ label, value, sub, icon, color, bg }: StatCard) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
    <div className={`${bg} p-3 rounded-lg`}>
      <div className={color}>{icon}</div>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm font-medium text-gray-700 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [act, mat, mch, tls, hr] = await Promise.allSettled([
          activityService.getStats(),
          materialService.getStats(),
          machineryService.getStats(),
          toolsService.getStats(),
          hrService.getStats(),
        ]);
        setStats({
          activity: act.status === 'fulfilled' ? act.value.data.data : null,
          material: mat.status === 'fulfilled' ? mat.value.data.data : null,
          machinery: mch.status === 'fulfilled' ? mch.value.data.data : null,
          tools: tls.status === 'fulfilled' ? tls.value.data.data : null,
          hr: hr.status === 'fulfilled' ? hr.value.data.data : null,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const fmt = (n: any) => n ? Number(n).toLocaleString() : '—';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.full_name?.split(' ')[0]} 👋
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          RB-PMS v3 — Rathna Bhoomi Developer, Bengaluru
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 h-24 animate-pulse">
              <div className="bg-gray-100 rounded h-4 w-1/2 mb-2" />
              <div className="bg-gray-100 rounded h-6 w-1/3" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Master Data Stats */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Master Data
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <StatCard
                label="Construction Activities"
                value={fmt(stats.activity?.total_activities)}
                sub={`${fmt(stats.activity?.stages)} stages · ${fmt(stats.activity?.processes)} processes`}
                icon={<ClipboardList size={20} />}
                color="text-blue-600" bg="bg-blue-50"
              />
              <StatCard
                label="Materials in Catalogue"
                value={fmt(stats.material?.total_materials)}
                sub={`${fmt(stats.material?.total_variants)} variants · ${fmt(stats.material?.total_suppliers)} suppliers`}
                icon={<Package size={20} />}
                color="text-emerald-600" bg="bg-emerald-50"
              />
              <StatCard
                label="Machinery Fleet"
                value={fmt(stats.machinery?.total)}
                sub={`${fmt(stats.machinery?.available)} available · ${fmt(stats.machinery?.on_site)} on-site`}
                icon={<Wrench size={20} />}
                color="text-orange-600" bg="bg-orange-50"
              />
              <StatCard
                label="Tools in Store"
                value={fmt(stats.tools?.total_tools)}
                sub={`${fmt(stats.tools?.available_qty)} qty available · ${fmt(stats.tools?.issued_qty)} issued`}
                icon={<Settings size={20} />}
                color="text-purple-600" bg="bg-purple-50"
              />
              <StatCard
                label="Employees"
                value={fmt(stats.hr?.total_active)}
                sub={`${fmt(stats.hr?.permanent)} permanent · ${fmt(stats.hr?.contract)} contract`}
                icon={<HardHat size={20} />}
                color="text-rose-600" bg="bg-rose-50"
              />
              <StatCard
                label="Labour Skills"
                value={fmt(stats.hr?.total_skills)}
                sub="Across all trade categories"
                icon={<TrendingUp size={20} />}
                color="text-indigo-600" bg="bg-indigo-50"
              />
            </div>
          </div>

          {/* Department Breakdown */}
          {stats.hr?.by_department && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Team by Department
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.hr.by_department).map(([dept, count]: any) => (
                    <div key={dept} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-gray-500">{dept}</span>
                      <span className="text-sm font-bold text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Material categories */}
          {stats.material?.by_category && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Materials by Category
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.material.by_category).map(([cat, count]: any) => (
                    <div key={cat} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-xs font-medium text-gray-500">{cat}</span>
                      <span className="text-sm font-bold text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardPage;
